import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { getStatusBadge, getImportSummary } from '../../../utils/importUtils';
import ValidationErrorsTable from './ValidationErrorsTable';
import DatabaseErrorsTable from './DatabaseErrorsTable';
import SkippedUsersTable from './SkippedUsersTable';

/**
 * ImportResultsModal displays the results of a user import operation in a modal dialog.
 * @component
 * @param {boolean} show - Controls the visibility of the modal.
 * @param {function} onHide - Callback to close the modal.
 * @param {Object} importResults - Results data from the import operation.
 * @param {string} importResults.message - Summary message about the import.
 * @param {Object} importResults.results - Detailed results including validation, database, and skipped users.
 * @param {function} onImportAnother - Callback to trigger another import operation.
 * @returns {JSX.Element|null} Modal displaying import results, or null if no results.
 */
const ImportResultsModal = ({ show, onHide, importResults, onImportAnother }) => {
  if (!importResults) return null;

  const summary = getImportSummary(importResults);
  const hasErrors = summary.totalErrors > 0 || summary.skippedCount > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div className="mb-4">
            <h6>Summary</h6>
            <div className="mb-2">
              {getStatusBadge('success', summary.successCount)}
              {getStatusBadge('errors', summary.totalErrors)}
              {getStatusBadge('skipped', summary.skippedCount)}
            </div>
            <Alert variant={summary.successCount > 0 ? 'success' : 'warning'}>
              {importResults.message}
            </Alert>
          </div>

          {hasErrors && (
            <div>
              <h6>Detailed Results</h6>

              <ValidationErrorsTable
                errors={importResults.results?.validation?.errors}
              />

              <DatabaseErrorsTable
                errors={importResults.results?.database?.errors}
              />

              <SkippedUsersTable
                skippedUsers={importResults.results?.validation?.skipped}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={onImportAnother}>
          Import Another File
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportResultsModal;
